import React from 'react'
import { StyleProp, View, ViewStyle } from 'react-native'
import { Button, Text } from '@src/components/base'
import { useNotificationInboxPresentation } from '@src/components/notifications/hooks/useNotificationInboxPresentation'
import { StyleService, useStyleSheet } from '@src/style/service'
import { AlertContent } from '@src/components/notifications/content/AlertContent'
import { NotificationContent } from '@src/components/notifications/content/NotificationContent'
import { useIsDarkMode } from '@src/config/theme'
import type { LiveNotificationBannerData } from '../hooks/useLiveNotificationBanner'

const NOTIFICATION_ACTION_ALIGNMENT = 52
const ALERT_ACTION_ALIGNMENT = 24
const ALERT_IMAGE_ACTION_ALIGNMENT = 40

interface BaseLiveSurfaceBannerBodyProps extends LiveNotificationBannerData {
  style?: StyleProp<ViewStyle>
  testIdPrefix: string
}

export const BaseLiveSurfaceBannerBody = ({
  notification,
  action,
  actionButtonLabel,
  dismissButtonLabel,
  dismiss,
  style,
  testIdPrefix,
}: BaseLiveSurfaceBannerBodyProps) => {
  const styles = useStyleSheet(themedStyles)
  const isDarkMode = useIsDarkMode()
  const presentation = useNotificationInboxPresentation({ notification })
  const alertBannerStyle = isDarkMode ? styles.alertBannerDark : styles.alertBannerBase
  const hasActions = !!dismiss || !!action
  const isDismissible = !!dismiss
  const isLeftAlignedActionOnly = !dismiss && !!action
  const alertActionAlignment = notification.imageUrl
    ? ALERT_IMAGE_ACTION_ALIGNMENT
    : ALERT_ACTION_ALIGNMENT

  return (
    <View
      style={[styles.banner, presentation.isAlert && alertBannerStyle, style]}
      testID={`${testIdPrefix}/${notification.id}`}
    >
      <View style={[styles.contentWrapper, hasActions && styles.contentWrapperWithActions]}>
        {presentation.isAlert ? (
          <AlertContent
            iconTestID={`${testIdPrefix}/AlertIcon/${notification.id}`}
            notification={notification}
            presentation={presentation}
          />
        ) : (
          <NotificationContent
            notification={notification}
            presentation={presentation}
            showUnreadDot={false}
          />
        )}
      </View>

      {hasActions && (
        <View
          style={[
            styles.actionsContainer,
            isDismissible && styles.actionsContainerRightAligned,
            isLeftAlignedActionOnly &&
              (presentation.isAlert
                ? { paddingLeft: alertActionAlignment }
                : styles.actionsContainerNotification),
          ]}
          testID={`${testIdPrefix}/Actions/${notification.id}`}
        >
          {dismiss && (
            <Button
              type="transparent"
              size="tiny"
              accessibilityLabel={dismissButtonLabel}
              onPress={dismiss}
            >
              <Text type="small" bold lineSpacing="none">
                {dismissButtonLabel}
              </Text>
            </Button>
          )}
          {action && (
            <Button
              type={isDismissible ? 'primary' : 'transparent'}
              size="tiny"
              accessibilityLabel={actionButtonLabel}
              onPress={action}
            >
              {isDismissible ? (
                actionButtonLabel
              ) : (
                <Text type="regular" bold lineSpacing="none" style={styles.linkActionText}>
                  {actionButtonLabel}
                </Text>
              )}
            </Button>
          )}
        </View>
      )}
    </View>
  )
}

const themedStyles = StyleService.create({
  banner: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.background',
    overflow: 'hidden',
  },
  alertBannerBase: {
    backgroundColor: 'theme.warning.lightest',
    borderColor: 'theme.warning.lighter',
    borderWidth: 1,
  },
  alertBannerDark: {
    backgroundColor: 'theme.surface.base1',
    borderColor: 'theme.warning.lighter',
    borderWidth: 1,
  },
  contentWrapper: {
    marginBottom: 0,
  },
  contentWrapperWithActions: {
    marginBottom: 6,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 8,
  },
  actionsContainerRightAligned: {
    justifyContent: 'flex-end',
  },
  actionsContainerNotification: {
    paddingLeft: NOTIFICATION_ACTION_ALIGNMENT,
  },
  linkActionText: {
    color: 'theme.text.link',
  },
})
